import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { formatMass, formatDate, relativeTime, titleCase } from '../../lib/format'
import type { MinerDashboard } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import {
  AlertIcon, BellIcon, ChartIcon, CheckIcon, InboxIcon, PlusIcon, ShieldIcon,
} from '../../components/icons'
import './dashboard.css'

export default function Dashboard() {
  const { user } = useAuth()
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['miner', 'dashboard'],
    queryFn: () => api.get<MinerDashboard>('/miner/dashboard'),
  })

  const firstName = (user?.full_name ?? user?.email ?? '').split(/[\s@]/)[0]

  if (isError) {
    return (
      <>
        <PageHead name={firstName} />
        <Alert tone="danger">
          Could not load your dashboard. {(error as Error).message}
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <Button size="sm" variant="secondary" onClick={() => refetch()}>Try again</Button>
          </div>
        </Alert>
      </>
    )
  }

  const onboarding = data?.onboarding
  const productions = data?.recent_productions ?? []
  const notifications = data?.recent_notifications ?? []
  const licenses = data?.active_licenses ?? []
  const notices = data?.compliance_notices ?? []

  const totalKg = productions.reduce((sum, p) => {
    const n = typeof p.volume_kg === 'number' ? p.volume_kg : Number(p.volume_kg)
    return sum + (Number.isNaN(n) ? 0 : n)
  }, 0)

  return (
    <>
      <PageHead name={firstName} />

      {/* Onboarding only earns space until it is finished. */}
      {onboarding && onboarding.progress < 3 && (
        <Card className="stack" style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader
            title="Finish setting up your account"
            sub={`${onboarding.progress} of 3 steps complete`}
          />
          <CardBody>
            <div className="onboard">
              <div className="onboard__bar">
                <div className="onboard__fill" style={{ width: `${(onboarding.progress / 3) * 100}%` }} />
              </div>
              <div className="onboard__steps">
                <Step done={onboarding.kyc_done} title="Verify identity"
                      body="Upload a national ID or driver's licence." to="/kyc" />
                {/* No `to`: joining is not self-service any more - a super
                    admin assigns the cooperative, so this step reports
                    status rather than offering an action. */}
                <Step done={onboarding.org_done} title="Cooperative membership"
                      body="The administrator assigns your cooperative." />
                <Step done={onboarding.licence_done} title="Active licence"
                      body="Your cooperative needs a valid mining licence." />
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      <div className="grid grid--stats" style={{ marginBottom: 'var(--sp-6)' }}>
        <Stat label="Recent output" value={isLoading ? null : formatMass(totalKg)}
              meta={`across ${productions.length} recent log${productions.length === 1 ? '' : 's'}`}
              icon={<ChartIcon />} />
        <Stat label="Active licences" value={isLoading ? null : String(licenses.length)}
              meta={licenses[0]?.expiry_date ? `next expiry ${formatDate(licenses[0].expiry_date)}` : 'none on file'}
              icon={<ShieldIcon />} />
        <Stat label="Unread alerts" value={isLoading ? null : String(data?.notifications_unread ?? 0)}
              meta={data?.notifications_unread ? 'needs your attention' : 'all caught up'}
              icon={<BellIcon />} />
        <Stat label="Compliance notices" value={isLoading ? null : String(notices.length)}
              meta={notices.length ? 'open against your cooperative' : 'nothing outstanding'}
              icon={<AlertIcon />} />
      </div>

      <div className="grid grid--2">
        <Card>
          <CardHeader
            title="Recent production"
            action={<Button size="sm" variant="secondary" icon={<PlusIcon />}
                            onClick={() => { window.location.href = '/production' }}>Log</Button>}
          />
          <CardBody flush>
            {isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack">
                <Skeleton h={18} /><Skeleton h={18} w="72%" /><Skeleton h={18} w="86%" />
              </div>
            ) : productions.length === 0 ? (
              <EmptyState
                icon={<ChartIcon size={22} />}
                title="No production logged yet"
                body="Record what you extract so your cooperative and the Ministry can see verified output."
                action={<Link to="/production"><Button size="sm">Log production</Button></Link>}
              />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Mineral</th><th className="table__num">Volume</th>
                      <th>Period</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productions.map((p, i) => (
                      <tr key={p.id ?? i}>
                        <td>{titleCase(p.mineral_type)}</td>
                        <td className="table__num">{formatMass(p.volume_kg)}</td>
                        <td>{formatDate(p.period_start)}</td>
                        <td><StatusBadge status={p.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Latest notifications"
            action={<Link to="/notifications" style={{ fontSize: 'var(--text-sm)' }}>View all</Link>}
          />
          <CardBody flush>
            {isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack">
                <Skeleton h={18} /><Skeleton h={18} w="64%" />
              </div>
            ) : notifications.length === 0 ? (
              <EmptyState icon={<InboxIcon />} title="Nothing new"
                          body="Alerts about safety reports, training, inspections and approvals will appear here." />
            ) : (
              <div className="feed">
                {notifications.map((n, i) => (
                  <div key={n.id ?? i} className={`feed__item${n.read ? '' : ' feed__item--unread'}`}>
                    <span className={`feed__dot${n.read ? ' feed__dot--read' : ''}`} aria-hidden />
                    <div>
                      <div className="feed__title">{n.title ?? titleCase(n.type) ?? 'Notification'}</div>
                      {(n.body ?? n.message) && <div className="feed__body">{n.body ?? n.message}</div>}
                      <div className="feed__time">{relativeTime(n.created_at ?? n.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function PageHead({ name }: { name: string }) {
  return (
    <div className="page-head">
      <div>
        <h1>Welcome back{name ? <>, <span className="welcome__name">{name}</span></> : ''}</h1>
        <p className="page-head__sub">Here&rsquo;s what needs your attention today.</p>
      </div>
    </div>
  )
}

function Stat({
  label, value, meta, icon,
}: { label: string; value: string | null; meta?: string; icon?: React.ReactNode }) {
  return (
    <Card hover>
      <div className="stat">
        <div className="stat__top">
          <span className="stat__label">{label}</span>
          {icon && <span className="stat__icon">{icon}</span>}
        </div>
        {value === null ? <Skeleton h={34} w="60%" /> : <div className="stat__value">{value}</div>}
        {meta && <div className="stat__meta">{meta}</div>}
      </div>
    </Card>
  )
}

function Step({
  done, title, body, to,
}: { done: boolean; title: string; body: string; to?: string }) {
  const inner = (
    <div className={`step${done ? ' step--done' : ''}`}>
      <span className="step__tick">{done ? <CheckIcon size={13} /> : ''}</span>
      <div>
        <div className="step__title">{title}</div>
        <div className="step__body">{body}</div>
      </div>
    </div>
  )
  return to && !done
    ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{inner}</Link>
    : inner
}
